import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import crmApi, { money, DOC_STATUS_META, fmtDate, fmtDateTime } from '../../utils/crmApi';
import useLiveRefresh from '../../utils/useLiveRefresh';
import DocumentPreview from './DocumentPreview';

export default function CrmDocumentDetail({ docType, routeBase, title }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useOutletContext();
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [users, setUsers] = useState([]);
  const [showSubmit, setShowSubmit] = useState(false);
  const [approverId, setApproverId] = useState('');
  const [showSchedule, setShowSchedule] = useState(false);
  const [sched, setSched] = useState({ schedule_type: 'date', day: '', date: '', handler_id: '' });
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [deleteModal, setDeleteModal] = useState(false);
  const [partialModal, setPartialModal] = useState(false);
  const [partialAmt, setPartialAmt] = useState('');
  const doReject = () => { setRejectModal(false); return action(() => crmApi.post(`/documents/${id}/reject`, { reason: rejectReason })); };
  const doDelete = () => { setDeleteModal(false); return action(async () => { await crmApi.delete(`/documents/${id}`); navigate(`/crm/${routeBase}`); }); };
  const doPartial = () => { setPartialModal(false); return action(() => crmApi.post(`/documents/${id}/payment`, { payment_status: 'partial', paid_amount: parseFloat(partialAmt) || 0 })); };
  const doSchedule = () => {
    setShowSchedule(false);
    return action(() => crmApi.post(`/documents/${id}/schedule`, sched));
  };

  function load() { crmApi.get(`/documents/${id}`).then(setDoc).catch(() => {}); }
  useEffect(() => { load(); }, [id]);
  // Silently refresh so status/approval changes from others appear without a reload.
  useLiveRefresh(() => { crmApi.get(`/documents/${id}`).then(setDoc).catch(() => {}); });
  useEffect(() => { crmApi.get('/users').then(u => setUsers(Array.isArray(u) ? u : [])).catch(() => {}); }, []);

  async function action(fn) {
    setBusy(true); setMsg(null);
    try { await fn(); load(); }
    catch (e) { setMsg({ type: 'error', text: e?.error || 'Action failed' }); }
    finally { setBusy(false); }
  }
  const doSubmit = () => { if (!approverId) return; setShowSubmit(false); return action(() => crmApi.post(`/documents/${id}/submit`, { approver_id: approverId })); };
  const approve = () => action(async () => {
    const r = await crmApi.post(`/documents/${id}/approve`);
    if (r && r.converted) setMsg({ type: 'ok', text: `Approved. Automatically created ${r.converted.doc_type === 'invoice' ? 'invoice' : 'service order'} ${r.converted.doc_number}.` });
  });
  const reject = () => { setRejectReason(''); setRejectModal(true); };
  const send = () => action(async () => { const r = await crmApi.post(`/documents/${id}/send`); if (r.email_pending) setMsg({ type: 'info', text: 'Marked as sent. Email delivery will be enabled once SMTP is wired up.' }); });
  const setPayment = (payment_status, paid_amount) => action(() => crmApi.post(`/documents/${id}/payment`, { payment_status, paid_amount }));
  const convert = (to) => action(async () => { const r = await crmApi.post(`/documents/${id}/convert`, { to }); const rb = to === 'invoice' ? 'invoices' : 'sales-orders'; navigate(`/crm/${rb}/${r.id}`); });
  const del = () => setDeleteModal(true);

  if (!doc) return <div style={{ color: 'var(--muted)' }}>Loading…</div>;
  const sm = DOC_STATUS_META[doc.status] || DOC_STATUS_META.draft;
  const isCreator = doc.created_by === user?.id;
  const isApprover = doc.approver_id === user?.id;
  const canEdit = ['draft', 'rejected'].includes(doc.status);
  const isInvoice = doc.doc_type === 'invoice';

  // Workflow buttons available depending on status / role
  const actions = [];
  actions.push({ label: 'View PDF', fn: () => window.open(`/api/crm/documents/${id}/pdf`, '_blank'), kind: 'plain' });
  actions.push({ label: 'Download Word', fn: () => window.open(`/api/crm/documents/${id}/docx`, '_blank'), kind: 'plain' });
  if (canEdit) actions.push({ label: 'Edit', fn: () => navigate(`/crm/${routeBase}/${id}/edit`), kind: 'plain' });
  if (canEdit) actions.push({ label: 'Submit for Approval', fn: () => { setApproverId(''); setShowSubmit(true); }, kind: 'primary' });
  if (doc.status === 'pending_approval') {
    const myTurn = (doc.approval_stage === 'creator' && isCreator) || (doc.approval_stage === 'approver' && isApprover);
    if (myTurn) {
      actions.push({ label: doc.approval_stage === 'creator' ? 'Confirm (Step 1)' : 'Approve (Final)', fn: approve, kind: 'green' });
      actions.push({ label: 'Reject', fn: reject, kind: 'danger' });
    }
  }
  if (isInvoice && doc.status === 'approved') actions.push({ label: 'Schedule Send', fn: () => { setSched({ schedule_type: doc.send_schedule_type && doc.send_schedule_type !== 'none' ? doc.send_schedule_type : 'date', day: doc.send_schedule_day || '', date: doc.send_schedule_date ? String(doc.send_schedule_date).slice(0, 10) : '', handler_id: doc.send_handler_id || '' }); setShowSchedule(true); }, kind: 'plain' });
  if (isInvoice && doc.status === 'approved') actions.push({ label: 'Send Now', fn: send, kind: 'primary' });
  if (isInvoice && doc.inventory_status === 'pending' && isAdmin) actions.push({ label: 'Approve Inventory', fn: () => action(() => crmApi.post(`/documents/${id}/inventory-approve`)), kind: 'green' });
  if (!isInvoice && ['draft', 'approved'].includes(doc.status)) actions.push({ label: 'Send to Customer', fn: send, kind: 'primary' });
  if (doc.doc_type === 'quote') actions.push({ label: 'Convert to Service Order', fn: () => convert('sales_order'), kind: 'plain' });
  if (doc.doc_type === 'quote') actions.push({ label: 'Convert to Invoice', fn: () => convert('invoice'), kind: 'plain' });
  if (doc.doc_type === 'sales_order') actions.push({ label: 'Convert to Invoice', fn: () => convert('invoice'), kind: 'plain' });
  if (canEdit && !doc.stock_committed) actions.push({ label: 'Delete', fn: del, kind: 'danger' });

  const btnStyle = (kind) => {
    if (kind === 'primary') return { background: '#0D9488', borderColor: '#0D9488', color: '#fff' };
    if (kind === 'green') return { background: '#16A34A', borderColor: '#16A34A', color: '#fff' };
    if (kind === 'danger') return { background: 'var(--surface)', borderColor: '#FCA5A5', color: '#DC2626' };
    return {};
  };

  return (
    <div>
      <button className="btn" style={{ marginBottom: '14px' }} onClick={() => navigate(`/crm/${routeBase}`)}>← {title}</button>

      {/* Header with status + workflow */}
      <div className="card" style={{ padding: '16px 18px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '17px', fontWeight: '800', fontFamily: 'var(--font-mono)' }}>{doc.doc_number}</div>
            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{doc.account_name || 'No account'} · {money(doc.total, doc.currency)}</div>
          </div>
          <span style={{ fontSize: '12px', fontWeight: '700', padding: '4px 12px', borderRadius: '99px', background: sm.bg, color: sm.color }}>{sm.label}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {actions.map((a, i) => (
              <button key={i} className="btn" disabled={busy} onClick={a.fn} style={btnStyle(a.kind)}>{a.label}</button>
            ))}
          </div>
        </div>
        {msg && <div style={{ marginTop: '10px', fontSize: '12px', color: msg.type === 'error' ? '#DC2626' : '#0D9488' }}>{msg.text}</div>}
      </div>

      {/* Two-column: preview (left, fills) + workflow & activity (right) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: '14px', alignItems: 'start' }}>
        <div style={{ minWidth: 0 }}>
          <DocumentPreview doc={doc} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Workflow status */}
          <div className="card" style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: '13px', fontWeight: '800', marginBottom: '10px' }}>Status</div>
            <div style={{ padding: '10px 12px', background: 'var(--surface2)', borderRadius: 'var(--radius)', fontSize: '12px', color: 'var(--muted2)', lineHeight: 1.5 }}>
              {doc.status === 'draft' && '📝 Draft — edit, then submit for approval.'}
              {doc.status === 'pending_approval' && doc.approval_stage === 'creator' && `⏳ Step 1 — waiting for the creator (${doc.created_by_name || 'creator'}) to approve. Then it goes to ${doc.approver_name || 'the assigned approver'}.`}
              {doc.status === 'pending_approval' && doc.approval_stage === 'approver' && `⏳ Step 2 — creator approved${doc.creator_approved_by_name ? ` by ${doc.creator_approved_by_name}` : ''}. Now waiting for ${doc.approver_name || 'the assigned approver'} to give final approval.`}
              {doc.status === 'approved' && `✅ Approved by ${doc.approved_by_name || 'approver'}${doc.approved_at ? ' on ' + fmtDate(doc.approved_at) : ''} — ready to send to the customer.`}
              {isInvoice && doc.status === 'approved' && doc.send_schedule_type === 'date' && doc.send_schedule_date && ` 📅 Scheduled to send on ${fmtDate(doc.send_schedule_date)}${doc.handler_name ? ' (handler: ' + doc.handler_name + ')' : ''}.`}
              {isInvoice && doc.status === 'approved' && doc.send_schedule_type === 'monthly' && doc.send_schedule_day && ` 📅 Scheduled to send on day ${doc.send_schedule_day} each month${doc.handler_name ? ' (handler: ' + doc.handler_name + ')' : ''}.`}
              {doc.status === 'rejected' && `❌ Rejected${doc.rejected_reason ? ': ' + doc.rejected_reason : ''}. Edit and resubmit.`}
              {doc.status === 'sent' && `📤 Sent to ${doc.sent_to || 'customer'}${doc.sent_at ? ' on ' + fmtDate(doc.sent_at) : ''}.${isInvoice ? ' Stock has been deducted.' : ''}`}
              {isInvoice && doc.inventory_status === 'pending' && ' ⚠️ Hardware items are awaiting inventory approval before stock is released.'}
              {doc.status === 'paid' && `💰 Paid${doc.paid_at ? ' on ' + fmtDate(doc.paid_at) : ''}.`}
            </div>
          </div>

          {/* Collection (invoices, once sent) */}
          {isInvoice && ['sent', 'paid'].includes(doc.status) && (
            <div className="card" style={{ padding: '16px 18px' }}>
              <div style={{ fontSize: '13px', fontWeight: '800', marginBottom: '10px' }}>Collection</div>
              <div style={{ fontSize: '12px', color: 'var(--muted2)', marginBottom: '10px' }}>
                Status: <strong style={{ color: doc.payment_status === 'paid' ? '#16A34A' : doc.payment_status === 'partial' ? '#D97706' : '#DC2626' }}>
                  {doc.payment_status === 'paid' ? 'Paid in full' : doc.payment_status === 'partial' ? `Partially paid (${money(doc.paid_amount, doc.currency)})` : 'Unpaid'}
                </strong>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button className="btn btn-sm" disabled={busy} onClick={() => setPayment('paid')} style={{ background: '#16A34A', color: '#fff', borderColor: '#16A34A' }}>Mark Paid</button>
                <button className="btn btn-sm" disabled={busy} onClick={() => { setPartialAmt(''); setPartialModal(true); }}>Partial</button>
                <button className="btn btn-sm" disabled={busy} onClick={() => setPayment('unpaid')}>Unpaid</button>
              </div>
            </div>
          )}

          {/* Activity log */}
          <div className="card" style={{ padding: '18px' }}>
            <div style={{ fontSize: '14px', fontWeight: '800', marginBottom: '12px' }}>Document History</div>
            {doc.logs?.length === 0 && <div style={{ fontSize: '12px', color: 'var(--muted)' }}>No activity yet.</div>}
            {doc.logs?.map(l => (
              <div key={l.id} style={{ display: 'flex', gap: '10px', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#7C3AED', marginTop: '5px', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'capitalize' }}>{l.action.replace(/_/g, ' ')}</div>
                  {l.note && <div style={{ fontSize: '12px', color: 'var(--muted2)' }}>{l.note}</div>}
                  <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{l.user_name} · {fmtDateTime(l.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Submit for approval modal */}
      {showSubmit && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowSubmit(false); }}>
          <div className="modal" style={{ width: '440px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '8px' }}>Submit for Approval</h3>
            <p style={{ fontSize: '12px', color: 'var(--muted2)', lineHeight: 1.6, marginBottom: '14px' }}>
              Choose who gives final approval. The flow is: <strong>you approve first</strong> (Step 1), then the person you pick gives <strong>final approval</strong> (Step 2) before it can be sent.
            </p>
            <div className="form-group">
              <label className="form-label">Approver</label>
              <select className="select" value={approverId} onChange={e => setApproverId(e.target.value)}>
                <option value="">Select an approver…</option>
                {users.filter(u => u.id !== user?.id).map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
              <button className="btn" onClick={() => setShowSubmit(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={!approverId} onClick={doSubmit}>Submit</button>
            </div>
          </div>
        </div>
      )}
      {/* Reject modal */}
      {rejectModal && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setRejectModal(false); }}>
          <div className="modal" style={{ width: '420px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '800', marginBottom: '8px' }}>Reject document</h3>
            <p style={{ fontSize: '12px', color: 'var(--muted2)', marginBottom: '12px' }}>Add a short reason so the creator knows what to fix. It goes back to draft for editing.</p>
            <textarea className="input" rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason for rejection…" style={{ marginBottom: '12px' }} />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setRejectModal(false)}>Cancel</button>
              <button className="btn" style={{ background: '#DC2626', color: '#fff', borderColor: '#DC2626' }} onClick={doReject}>Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setDeleteModal(false); }}>
          <div className="modal" style={{ width: '380px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '800', marginBottom: '8px' }}>Delete this document?</h3>
            <p style={{ fontSize: '13px', color: 'var(--muted2)', lineHeight: 1.6, marginBottom: '14px' }}>This permanently removes the document. This can't be undone.</p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setDeleteModal(false)}>Cancel</button>
              <button className="btn" style={{ background: '#DC2626', color: '#fff', borderColor: '#DC2626' }} onClick={doDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Partial payment modal */}
      {partialModal && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setPartialModal(false); }}>
          <div className="modal" style={{ width: '380px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '800', marginBottom: '8px' }}>Record partial payment</h3>
            <p style={{ fontSize: '12px', color: 'var(--muted2)', marginBottom: '12px' }}>Enter the amount received so far.</p>
            <input className="input" type="number" value={partialAmt} onChange={e => setPartialAmt(e.target.value)} placeholder="Amount paid" style={{ marginBottom: '12px' }} autoFocus />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setPartialModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={doPartial}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule send modal */}
      {showSchedule && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowSchedule(false); }}>
          <div className="modal" style={{ width: '460px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '8px' }}>Schedule Invoice Send</h3>
            <p style={{ fontSize: '12px', color: 'var(--muted2)', lineHeight: 1.6, marginBottom: '14px' }}>
              Choose when this invoice should go to the customer. On the scheduled day the assigned handler is emailed to review and release it.
            </p>
            <div className="form-group">
              <label className="form-label">Schedule type</label>
              <select className="select" value={sched.schedule_type} onChange={e => setSched({ ...sched, schedule_type: e.target.value })}>
                <option value="date">One specific date</option>
                <option value="monthly">Every month on a chosen day</option>
              </select>
            </div>
            {sched.schedule_type === 'date' ? (
              <div className="form-group">
                <label className="form-label">Send date</label>
                <input className="input" type="date" value={sched.date} onChange={e => setSched({ ...sched, date: e.target.value })} />
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Day of month (1–31)</label>
                <input className="input" type="number" min="1" max="31" value={sched.day} onChange={e => setSched({ ...sched, day: e.target.value })} placeholder="e.g. 1 for the 1st" />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Invoice handler <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(who is emailed & releases the send)</span></label>
              <select className="select" value={sched.handler_id} onChange={e => setSched({ ...sched, handler_id: e.target.value })}>
                <option value="">Select a handler…</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
              <button className="btn" onClick={() => setShowSchedule(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={doSchedule}>Save Schedule</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const QuoteDetail = () => <CrmDocumentDetail docType="quote" routeBase="quotes" title="Quotes" />;
export const SalesOrderDetail = () => <CrmDocumentDetail docType="sales_order" routeBase="sales-orders" title="Service Orders" />;
export const InvoiceDetail = () => <CrmDocumentDetail docType="invoice" routeBase="invoices" title="Invoices" />;
